import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { NoData } from "@/components/ui/no-data";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { microPromptService } from "@/services/microPromptService";
import { MicroPrompt, MicroPromptFormData } from "@/types/microPrompt";
import { zodResolver } from "@hookform/resolvers/zod";
import { MessageSquarePlus, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { supabase } from "../../../supabase/supabase";

// Form schema for micro prompt
const formSchema = z.object({
  title: z
    .string()
    .min(3, { message: "Title must be at least 3 characters" })
    .max(100, { message: "Title must be less than 100 characters" }),
  content: z
    .string()
    .min(5, { message: "Content must be at least 5 characters" })
    .max(500, { message: "Content must be less than 500 characters" }),
});

interface MicroPromptsContentProps {
  organizationId: string;
  onAddClickCallback?: (callback: () => void) => void;
}

export default function MicroPromptsContent({
  organizationId,
  onAddClickCallback,
}: MicroPromptsContentProps) {
  const [microPrompts, setMicroPrompts] = useState<MicroPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<MicroPrompt | null>(null);
  const [deletePromptId, setDeletePromptId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // Initialize form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      content: "",
    },
  });

  // Get current user ID
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        setUserId(data.user.id);
      }
    };
    getCurrentUser();
  }, []);

  // Register the callback function for opening the dialog
  useEffect(() => {
    if (onAddClickCallback) {
      onAddClickCallback(() => {
        form.reset();
        setEditingPrompt(null);
        setIsDialogOpen(true);
      });
    }
  }, [onAddClickCallback, form]);

  // Fetch micro prompts
  const fetchMicroPrompts = async () => {
    if (!organizationId) return;
    setIsLoading(true);
    try {
      const data = await microPromptService.getMicroPrompts(organizationId);
      setMicroPrompts(data);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to fetch micro prompts",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMicroPrompts();
  }, [organizationId]);

  // Handle form submission
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!organizationId || !userId) return;

    try {
      if (editingPrompt) {
        // Update existing prompt
        await microPromptService.updateMicroPrompt(editingPrompt.id, values as MicroPromptFormData);
        toast({
          title: "Success",
          description: "Micro prompt updated successfully",
        });
      } else {
        // Create new prompt
        await microPromptService.createMicroPrompt(
          organizationId,
          values as MicroPromptFormData,
          userId
        );
        toast({
          title: "Success",
          description: "Micro prompt created successfully",
        });
      }
      // Reset form and close dialog
      form.reset();
      setIsDialogOpen(false);
      setEditingPrompt(null);
      // Refresh the list
      fetchMicroPrompts();
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.message ||
          (editingPrompt
            ? "Failed to update micro prompt"
            : "Failed to create micro prompt"),
        variant: "destructive",
      });
    }
  };

  // Handle edit button click
  const handleEdit = (prompt: MicroPrompt) => {
    setEditingPrompt(prompt);
    form.reset({
      title: prompt.title,
      content: prompt.content,
    });
    setIsDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deletePromptId) return;

    try {
      await microPromptService.deleteMicroPrompt(deletePromptId);
      toast({
        title: "Success",
        description: "Micro prompt deleted successfully",
      });
      // Refresh the list
      fetchMicroPrompts();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete micro prompt",
        variant: "destructive",
      });
    } finally {
      setDeletePromptId(null);
    }
  };

  // Reset form when dialog is closed
  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      form.reset();
      setEditingPrompt(null);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Available Micro Prompts</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner text="Loading micro prompts..." />
            </div>
          ) : microPrompts.length === 0 ? (
            <NoData
              icon={
                <MessageSquarePlus className="h-12 w-12 text-muted-foreground/50" />
              }
              title="No micro prompts found"
              description="Create your first micro prompt to get started."
              actionButton={
                <Button
                  variant="primaryOutline"
                  onClick={() => {
                    form.reset();
                    setEditingPrompt(null);
                    setIsDialogOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Micro Prompt
                </Button>
              }
            />
          ) : (
            <div className="space-y-4">
              {microPrompts.map((prompt) => (
                <div
                  key={prompt.id}
                  className="border rounded-lg p-4 space-y-2"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 flex-1">
                      <h4 className="font-medium">{prompt.title}</h4>
                      <p className="text-sm text-muted-foreground">
                        {prompt.content}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => handleEdit(prompt)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => setDeletePromptId(prompt.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingPrompt ? "Edit Micro Prompt" : "Add Micro Prompt"}
            </DialogTitle>
            <DialogDescription>
              {editingPrompt
                ? "Update the details of your micro prompt."
                : "Create a new reusable prompt snippet for your organization."}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="E.g., Format Currency in Millions"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      A descriptive title for your micro prompt.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="E.g., Format all currency values in millions (e.g., $1.2M instead of $1,200,000)."
                        className="min-h-24"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      The actual prompt snippet that will be inserted into
                      assistant prompts.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="submit">
                  {editingPrompt ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletePromptId}
        onOpenChange={(open) => !open && setDeletePromptId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this micro prompt. This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}